// Runtime validator for canonical + hreflang.
// Runs in dev permanently, in prod only when ?seo-debug=1 (or localStorage.seoDebug='1').
// Reports issues via console (grouped) and exposes window.__seoAudit for inspection.

export interface SeoAuditIssue {
  level: 'error' | 'warn';
  code: string;
  message: string;
}

export interface SeoAuditReport {
  url: string;
  language: 'fr' | 'en' | 'es' | 'unknown';
  canonical: string | null;
  alternates: { hreflang: string; href: string }[];
  issues: SeoAuditIssue[];
  passed: boolean;
  timestamp: number;
}

const REQUIRED_LOCALES = ['fr', 'en', 'es'];
const VALID_HREFLANG = /^(x-default|[a-z]{2}(-[A-Z]{2})?)$/;

function detectLanguage(): SeoAuditReport['language'] {
  const path = window.location.pathname;
  if (path.startsWith('/en/') || path.startsWith('/en') && path === '/en') return 'en';
  if (path.startsWith('/es/') || path === '/es') return 'es';
  if (/^\/(en|es)(\/|$)/.test(path)) return path.startsWith('/en') ? 'en' : 'es';
  return 'fr';
}

export function runSeoCanonicalHreflangAudit(): SeoAuditReport {
  const issues: SeoAuditIssue[] = [];
  const head = document.head;

  // ---- Canonical ----
  const canonicalNodes = Array.from(
    head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]')
  );
  const canonical = canonicalNodes[0]?.href || null;

  if (canonicalNodes.length === 0) {
    issues.push({ level: 'error', code: 'CANONICAL_MISSING', message: 'No <link rel="canonical"> found.' });
  } else if (canonicalNodes.length > 1) {
    const hrefs = canonicalNodes.map((n) => n.href);
    issues.push({
      level: 'error',
      code: 'CANONICAL_DUPLICATE',
      message: `${canonicalNodes.length} canonical tags found → ${hrefs.join(' | ')}`,
    });
  }

  if (canonical) {
    try {
      const u = new URL(canonical);
      if (u.protocol !== 'https:') {
        issues.push({ level: 'warn', code: 'CANONICAL_PROTOCOL', message: `Canonical not HTTPS: ${canonical}` });
      }
      if (!/(^|\.)rankserveur\.com$/.test(u.hostname) && !/lovable\.app$/.test(u.hostname)) {
        issues.push({
          level: 'warn',
          code: 'CANONICAL_HOST',
          message: `Canonical points off-brand host: ${u.hostname}`,
        });
      }
    } catch {
      issues.push({ level: 'error', code: 'CANONICAL_INVALID_URL', message: `Canonical not a valid URL: ${canonical}` });
    }
  }

  // ---- Hreflang ----
  const altNodes = Array.from(
    head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')
  );
  const alternates = altNodes.map((n) => ({
    hreflang: (n.getAttribute('hreflang') || '').trim(),
    href: n.href,
  }));

  // No alternates is acceptable on non-multilingual routes (admin, auth).
  // Audit only if at least one alternate is present OR route is clearly multilingual.
  const path = window.location.pathname;
  const isMultilingualRoute =
    alternates.length > 0 ||
    /^\/(en|es)?\/?(server|serveur|servidor|game|juego|jeu)/.test(path) ||
    path === '/' || path === '/en' || path === '/es';

  if (isMultilingualRoute) {
    // Validate format
    for (const a of alternates) {
      if (!VALID_HREFLANG.test(a.hreflang)) {
        issues.push({
          level: 'warn',
          code: 'HREFLANG_FORMAT',
          message: `Invalid hreflang code: "${a.hreflang}"`,
        });
      }
    }

    // Detect duplicates (same hreflang twice with different hrefs)
    const byCode = new Map<string, Set<string>>();
    for (const a of alternates) {
      if (!byCode.has(a.hreflang)) byCode.set(a.hreflang, new Set());
      byCode.get(a.hreflang)!.add(a.href);
    }
    for (const [code, hrefs] of byCode) {
      if (hrefs.size > 1) {
        issues.push({
          level: 'error',
          code: 'HREFLANG_DUPLICATE',
          message: `hreflang "${code}" points to ${hrefs.size} different URLs: ${[...hrefs].join(' | ')}`,
        });
      }
    }

    // Required locales present (primary 2-letter codes)
    const baseCodes = new Set([...byCode.keys()].map((c) => c.split('-')[0]));
    for (const loc of REQUIRED_LOCALES) {
      if (!baseCodes.has(loc)) {
        issues.push({
          level: 'warn',
          code: 'HREFLANG_LOCALE_MISSING',
          message: `Missing hreflang for required locale "${loc}".`,
        });
      }
    }

    // x-default
    if (!byCode.has('x-default')) {
      issues.push({
        level: 'warn',
        code: 'HREFLANG_XDEFAULT_MISSING',
        message: 'Missing hreflang="x-default".',
      });
    }

    // Canonical must equal one of the alternates' href (self-reference)
    if (canonical && alternates.length > 0) {
      const allHrefs = new Set(alternates.map((a) => a.href));
      if (!allHrefs.has(canonical)) {
        issues.push({
          level: 'warn',
          code: 'CANONICAL_NOT_IN_ALTERNATES',
          message: `Canonical (${canonical}) is not listed in hreflang alternates → risk of conflicting signal.`,
        });
      }
    }

    // All alternates same origin as canonical
    if (canonical) {
      try {
        const canonOrigin = new URL(canonical).origin;
        for (const a of alternates) {
          try {
            if (new URL(a.href).origin !== canonOrigin) {
              issues.push({
                level: 'warn',
                code: 'HREFLANG_ORIGIN_MISMATCH',
                message: `hreflang "${a.hreflang}" on different origin (${a.href}) than canonical (${canonOrigin}).`,
              });
            }
          } catch {
            issues.push({
              level: 'error',
              code: 'HREFLANG_INVALID_URL',
              message: `hreflang "${a.hreflang}" has invalid URL: ${a.href}`,
            });
          }
        }
      } catch {/* canonical invalidity already reported */}
    }
  }

  const report: SeoAuditReport = {
    url: window.location.href,
    language: detectLanguage(),
    canonical,
    alternates,
    issues,
    passed: issues.filter((i) => i.level === 'error').length === 0,
    timestamp: Date.now(),
  };

  return report;
}

declare global {
  interface Window {
    __seoAudit?: SeoAuditReport;
    __seoAuditEnabled?: boolean;
  }
}

let scheduled = false;
let lastUrl = '';

function shouldRun(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.DEV) return true;
  if (window.__seoAuditEnabled) return true;
  const params = new URLSearchParams(window.location.search);
  if (params.get('seo-debug') === '1') {
    window.__seoAuditEnabled = true;
    try { localStorage.setItem('seoDebug', '1'); } catch {}
    return true;
  }
  try { if (localStorage.getItem('seoDebug') === '1') return true; } catch {}
  return false;
}

function logReport(report: SeoAuditReport) {
  const label = `[SEO Audit] ${report.language.toUpperCase()} · ${report.url}`;
  if (report.issues.length === 0) {
    // eslint-disable-next-line no-console
    console.log(`%c${label} ✓ OK`, 'color:#10b981;font-weight:bold');
    return;
  }
  const errors = report.issues.filter((i) => i.level === 'error');
  const warns = report.issues.filter((i) => i.level === 'warn');
  // eslint-disable-next-line no-console
  console.groupCollapsed(
    `%c${label} — ${errors.length} error(s), ${warns.length} warn(s)`,
    `color:${errors.length ? '#ef4444' : '#f59e0b'};font-weight:bold`
  );
  // eslint-disable-next-line no-console
  console.log('canonical:', report.canonical);
  // eslint-disable-next-line no-console
  console.log('alternates:', report.alternates);
  for (const issue of report.issues) {
    const fn = issue.level === 'error' ? console.error : console.warn;
    fn(`[${issue.code}] ${issue.message}`);
  }
  // eslint-disable-next-line no-console
  console.groupEnd();
}

/** Schedule an audit after Helmet has had time to mutate <head>. Debounced + dedupes per URL. */
export function scheduleSeoAudit(force = false) {
  if (!shouldRun()) return;
  if (scheduled) return;
  scheduled = true;
  setTimeout(() => {
    scheduled = false;
    const url = window.location.href;
    if (!force && url === lastUrl) return;
    lastUrl = url;
    const report = runSeoCanonicalHreflangAudit();
    window.__seoAudit = report;
    logReport(report);
  }, 800);
}
